/**
 * The run-on banner (CLA-1564): "you have another recording running".
 *
 * A recording left running can run straight into the NEXT patient's consult,
 * and silence cannot catch that — turnover between consults is ~15 min, a safe
 * silence stop is 60. What can: while this capture is live, the server names
 * the same clinician's OTHER live recordings that have been quiet for 10+
 * minutes (`other_live_recordings` on the capture-heartbeat response,
 * claro-elixir #1435).
 *
 * John's decision: a PASSIVE banner. It never stops anything and offers no
 * Stop — two devices in two rooms (numbing cream in one, the next patient in
 * the other) is a legitimate way to work. It only makes sure the clinician
 * knows.
 *
 * Pure: parsing, dismissal bookkeeping and the sentence. The store owns when.
 */
import { formatTime } from '@/utils/dateFormat'

export interface LiveRecordingSummary {
  note_id: number
  started_at: string
  silent_seconds: number
  client: string | null
}

/**
 * Read `other_live_recordings` from a heartbeat response.
 *
 * Returns null — "no information, change nothing" — when the field is missing
 * or not a list: an older server (`{status:"ok"}`), a failed heartbeat, a
 * garbled body. An entry that does not have the shape is dropped on its own,
 * so one bad row cannot hide the good ones. Never throws: nothing here may
 * break the heartbeat.
 */
export function parseOtherLiveRecordings(response: unknown): LiveRecordingSummary[] | null {
  if (typeof response !== 'object' || response === null) return null
  const list = (response as { other_live_recordings?: unknown }).other_live_recordings
  if (!Array.isArray(list)) return null
  const out: LiveRecordingSummary[] = []
  for (const item of list) {
    if (typeof item !== 'object' || item === null) continue
    const { note_id, started_at, silent_seconds, client } = item as Record<string, unknown>
    if (typeof note_id !== 'number' || !Number.isInteger(note_id)) continue
    if (typeof started_at !== 'string' || Number.isNaN(Date.parse(started_at))) continue
    if (typeof silent_seconds !== 'number' || !Number.isFinite(silent_seconds) || silent_seconds < 0) continue
    out.push({ note_id, started_at, silent_seconds, client: typeof client === 'string' ? client : null })
  }
  return out
}

/**
 * Keep a dismissal only for recordings still on the list. One that drops off
 * and later comes back is news again, so it shows again.
 */
export function reconcileDismissed(dismissed: number[], list: LiveRecordingSummary[]): number[] {
  const live = new Set(list.map((r) => r.note_id))
  return dismissed.filter((id) => live.has(id))
}

/** The recordings the banner should speak about, oldest first. */
export function visibleRunOns(list: LiveRecordingSummary[], dismissed: number[]): LiveRecordingSummary[] {
  const hidden = new Set(dismissed)
  return list
    .filter((r) => !hidden.has(r.note_id))
    .sort((a, b) => Date.parse(a.started_at) - Date.parse(b.started_at))
}

/**
 * Where the other recording is running, from the heartbeat's `client`, as the
 * clinician would say it. An open set on the server: anything unrecognised —
 * or null — names no device rather than guessing.
 */
export function deviceLabel(client: string | null | undefined): string | null {
  switch (client) {
    case 'ios_app':
      return 'on iOS'
    case 'android_app':
      return 'on Android'
    case 'web_desktop':
    case 'web_ios':
    case 'web_android':
      return 'on the web'
    default:
      return null
  }
}

export const RUN_ON_TITLE = 'You have another recording running'

/**
 * The banner's two lines, about the OLDEST recording (the likeliest to have
 * been forgotten):
 *   You have another recording running
 *   Started 2:15 pm on iOS · quiet for 22 min
 * with " · and N more" when there are several. Null when there is nothing to say.
 */
export function runOnBannerContent(
  visible: LiveRecordingSummary[],
): { title: string; detail: string } | null {
  if (visible.length === 0) return null
  const oldest = visible[0]
  const device = deviceLabel(oldest.client)
  const quietMin = Math.floor(oldest.silent_seconds / 60)
  const more = visible.length - 1
  const detail =
    `Started ${formatTime(oldest.started_at)}${device ? ` ${device}` : ''} · quiet for ${quietMin} min` +
    (more > 0 ? ` · and ${more} more` : '')
  return { title: RUN_ON_TITLE, detail }
}
